import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import CatchForm from "./CatchForm";
import OpinionForm from "./OpinionForm";
import ComingSoonPopup from "../ComingSoonPopup";
import useCreatePostFormResources from "./useCreatePostFormResources";
import { showRecipePostTab } from "../../config/featureVisibility";
import { favoriteSpotMapEventDetail } from "../../features/favoriteSpots/favoriteSpotOutlookContext";
import "../../styles/components/CreatePost.css";

const CREATE_POST_CLOSE_REQUEST_EVENT = "reelitpro:create-post-close-request";

function CreatePostModal({
  pickedCoordinates,
  onClose,
  onAddPost,
  isSubmitting = false,
  onSubmittingChange,
  isSimpleModeActive = false,
  user,
  onUserUpdated,
  favoriteSpots,
  showFavorites,
  setShowFavorites,
  setActiveTab,
  setShowProfile,
  draftCatchMedia,
  setDraftCatchMedia,
  draftCatchFormState,
  setDraftCatchFormState,
  draftExifDate,
  setDraftExifDate,
  draftExifTime,
  setDraftExifTime,
}) {
  const { t } = useTranslation();
  const [postType, setPostType] = useState("catch");
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [pendingCloseOptions, setPendingCloseOptions] = useState(null);
  const {
    rodDatabase,
    reelDatabase,
    braidDatabase,
    leaderDatabase,
    lureDatabase,
    hookBrandDatabase,
    baitDatabase,
    liveBaitDatabase,
    deadBaitDatabase,
    fishingMethods,
    fishList,
    lureSizeOptions,
    combinedHookOptions,
  } = useCreatePostFormResources();

  useLayoutEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    const scrollY = window.scrollY;
    const { style } = document.body;
    const previousBodyStyles = {
      position: style.position,
      top: style.top,
      left: style.left,
      right: style.right,
      width: style.width,
    };

    style.position = "fixed";
    style.top = `-${scrollY}px`;
    style.left = "0";
    style.right = "0";
    style.width = "100%";

    return () => {
      style.position = previousBodyStyles.position;
      style.top = previousBodyStyles.top;
      style.left = previousBodyStyles.left;
      style.right = previousBodyStyles.right;
      style.width = previousBodyStyles.width;
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    if (!isSimpleModeActive) {
      return;
    }

    setShowComingSoon(false);
    if (postType !== "catch") {
      setPostType("catch");
    }
  }, [isSimpleModeActive, postType]);

  const closeWithoutPrompt = useCallback(
    (options = {}) => {
      options.onBeforeClose?.();
      onClose?.(options);
    },
    [onClose]
  );

  const requestClose = useCallback(
    (options = {}) => {
      if (isSubmitting && !options.force) {
        return;
      }

      if (options.force || !isFormDirty) {
        closeWithoutPrompt(options);
        return;
      }

      setPendingCloseOptions(options);
    },
    [closeWithoutPrompt, isFormDirty, isSubmitting]
  );

  useEffect(() => {
    const handleCloseRequest = (event) => {
      requestClose({ reason: event?.detail?.reason || "external_close" });
    };

    window.addEventListener(CREATE_POST_CLOSE_REQUEST_EVENT, handleCloseRequest);
    return () => {
      window.removeEventListener(
        CREATE_POST_CLOSE_REQUEST_EVENT,
        handleCloseRequest
      );
    };
  }, [requestClose]);

  const handleConfirmClose = () => {
    const options = pendingCloseOptions || {};
    setPendingCloseOptions(null);
    closeWithoutPrompt(options);
  };

  const handleRecipeClick = (event) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }
    setShowComingSoon(true);
  };

  const handleFavoriteSpotOpen = (spot) => {
    if (isSubmitting) {
      return;
    }

    setActiveTab("map");
    setShowProfile(false);
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("flyToSpot", {
          detail: favoriteSpotMapEventDetail(spot),
        })
      );
    }, 400);
  };

  // Recipe posts are intentionally hidden temporarily; restore this gate when the recipe flow is ready.
  const isRecipePostTabTemporarilyHidden = true;
  const shouldShowRecipeTab =
    !isRecipePostTabTemporarilyHidden && showRecipePostTab;

  const submitStatusTitle =
    postType === "catch"
      ? t("create_post_submitting_catch_title", {
          defaultValue: "Uploading catch post...",
        })
      : t("create_post_submitting_opinion_title", {
          defaultValue: "Posting update...",
        });
  const submitStatusMessage = t("create_post_submitting_hint", {
    defaultValue:
      "Please wait while your post is uploaded. Controls are temporarily locked to prevent duplicates.",
  });

  return (
    <div
      className={`create-post-modal ${
        postType === "opinion" ? "modal-narrow" : "modal-wide"
      }${isSubmitting ? " is-submitting" : ""}`}
      onClick={(event) => event.stopPropagation()}
      aria-busy={isSubmitting}
    >
      <div className="create-post-header">
        <div className="create-post-header-main">
          <button
            type="button"
            className="create-post-close-x"
            onClick={() => requestClose({ reason: "top_close" })}
            disabled={isSubmitting}
            aria-label={t("close_create_post", {
              defaultValue: "Close create post",
            })}
          >
            &times;
          </button>
          <div className="create-post-heading-copy">
            <h2 className="create-post-title">{t("create_post_title")}</h2>
            <p className="create-post-subtitle">
              {postType === "catch"
                ? t("create_post_catch_subtitle", {
                    defaultValue: "Share your catch with the community.",
                  })
                : t("create_post_opinion_subtitle", {
                    defaultValue: "Share your experience, opinion, or ask for advice.",
                  })}
            </p>
          </div>
        </div>

        <div className="create-post-type-buttons">
          <button
            type="button"
            onClick={() => setPostType("catch")}
            className={`post-type-btn${postType === "catch" ? " active" : ""}${isSubmitting ? " disabled-btn" : ""}`}
            disabled={isSubmitting}
          >
            <span
              className="post-type-btn-icon post-type-btn-icon--catch"
              aria-hidden="true"
            />
            <span>{t("create_post_catch")}</span>
          </button>
          {!isSimpleModeActive && (
            <>
              <button
                type="button"
                onClick={() => setPostType("opinion")}
                className={`post-type-btn${postType === "opinion" ? " active" : ""}${isSubmitting ? " disabled-btn" : ""}`}
                disabled={isSubmitting}
              >
                <span
                  className="post-type-btn-icon post-type-btn-icon--opinion"
                  aria-hidden="true"
                />
                <span>{t("create_post_opinion")}</span>
              </button>
              {shouldShowRecipeTab ? (
                <button
                  type="button"
                  onClick={handleRecipeClick}
                  className="post-type-btn disabled-btn"
                  disabled={isSubmitting}
                >
                  <span
                    className="post-type-btn-icon post-type-btn-icon--recipe"
                    aria-hidden="true"
                  />
                  <span>{t("create_post_recipe_soon")}</span>
                </button>
              ) : null}

              {shouldShowRecipeTab && showComingSoon && (
                <ComingSoonPopup onClose={() => setShowComingSoon(false)} />
              )}
            </>
          )}
        </div>
      </div>

      {isSubmitting && (
        <div
          className="create-post-submit-status"
          role="status"
          aria-live="polite"
        >
          <span
            className="create-post-submit-status-spinner"
            aria-hidden="true"
          />
          <div className="create-post-submit-status-copy">
            <strong>{submitStatusTitle}</strong>
            <span>{submitStatusMessage}</span>
          </div>
        </div>
      )}

      {postType === "opinion" ? (
        <OpinionForm
          onClose={requestClose}
          onAddPost={onAddPost}
          onSubmittingChange={onSubmittingChange}
          onDirtyChange={setIsFormDirty}
        />
      ) : (
        <CatchForm
          pickedCoordinates={pickedCoordinates}
          onClose={requestClose}
          onAddPost={onAddPost}
          onSubmittingChange={onSubmittingChange}
          onDirtyChange={setIsFormDirty}
          isSimpleModeActive={isSimpleModeActive}
          user={user}
          onUserUpdated={onUserUpdated}
          draftCatchMedia={draftCatchMedia}
          setDraftCatchMedia={setDraftCatchMedia}
          draftCatchFormState={draftCatchFormState}
          setDraftCatchFormState={setDraftCatchFormState}
          draftExifDate={draftExifDate}
          setDraftExifDate={setDraftExifDate}
          draftExifTime={draftExifTime}
          setDraftExifTime={setDraftExifTime}
          rodDatabase={rodDatabase}
          reelDatabase={reelDatabase}
          braidDatabase={braidDatabase}
          leaderDatabase={leaderDatabase}
          lureDatabase={lureDatabase}
          hookBrandDatabase={hookBrandDatabase}
          baitDatabase={baitDatabase}
          liveBaitDatabase={liveBaitDatabase}
          deadBaitDatabase={deadBaitDatabase}
          fishingMethods={fishingMethods}
          fishList={fishList}
          lureSizeOptions={lureSizeOptions}
          combinedHookOptions={combinedHookOptions}
        />
      )}

      {pendingCloseOptions && (
        <div
          className="create-post-unsaved-backdrop"
          role="presentation"
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="create-post-unsaved-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-post-unsaved-title"
          >
            <h3 id="create-post-unsaved-title">
              {t("unsaved_post_close_title", {
                defaultValue: "You have unsaved post details. Close anyway?",
              })}
            </h3>
            <div className="create-post-unsaved-actions">
              <button
                type="button"
                className="create-post-unsaved-keep"
                onClick={() => setPendingCloseOptions(null)}
              >
                {t("keep_editing", { defaultValue: "Keep editing" })}
              </button>
              <button
                type="button"
                className="create-post-unsaved-close"
                onClick={handleConfirmClose}
              >
                {t("close_without_saving", {
                  defaultValue: "Close without saving",
                })}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFavorites && (
        <div
          onClick={() => setShowFavorites(false)}
          className="favorites-popup"
        >
          <h3 className="favorites-title">
            {"\u2764\uFE0F"} {t("favorite_spots")}
          </h3>
          {favoriteSpots.length === 0 ? (
            <p>{t("no_favorite_spots_hint")}</p>
          ) : (
            <ul className="favorites-list">
              {favoriteSpots.map((spot, index) => (
                <li key={index} className="favorite-item">
                  <strong>{spot.name}</strong>
                  <div className="fav-location-text">{spot.location}</div>
                  <div className="fav-coords-text">
                    {spot.lat?.toFixed(3)}, {spot.lng?.toFixed(3)}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleFavoriteSpotOpen(spot)}
                    className="open-location-btn"
                    disabled={isSubmitting}
                  >
                    {"\uD83D\uDDFA\uFE0F"} {t("open_location")}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            onClick={() => setShowFavorites(false)}
            className="close-favorites-btn"
            disabled={isSubmitting}
          >
            {t("close")}
          </button>
        </div>
      )}
    </div>
  );
}

export default CreatePostModal;
